import React, { useCallback, useContext, useEffect, useState } from 'react';
import {
  ReactFlow,
  MiniMap,
  Controls,
  Background,
  useNodesState,
  useEdgesState,
  addEdge,
  BackgroundVariant,
  Edge,
  MarkerType,
  Node,
  Handle,
  Position,
} from '@xyflow/react';
import '@xyflow/react/dist/style.css';
import { AccountingDepartmentNode } from './custom-nodes/accounting-department';
import { DriversNode } from './custom-nodes/drivers';
import { MerchantsNode } from './custom-nodes/merchants';
import { ExpenseNode } from './custom-nodes/expense-node';
import { Expense } from '../../../../interfaces/delivery-company/expenses';
import { useSnackBar } from '../../../../contexts/snackbar';
import axios from 'axios';
import { API_ENDPOINT } from '../../../../configurations/global.config';
import { AuthContext } from '../../../../contexts/user-context/user-context';
import { Button, CircularProgress, Typography } from '@mui/material';
import CloudDownloadIcon from '@mui/icons-material/CloudDownload';
import {
  add,
  displayMoney,
  minus,
  Money,
  moneyToMetric,
} from '../../../../utils/money';
import { DoubleDate } from '../../../common/double-date-picker/double-date-picker.interfaces';
import dayjs from 'dayjs';
import { DriverType } from '../../../../interfaces/delivery-company/drivers';
import { MerchantType } from '../../../../interfaces/delivery-company/merchants';
import {
  CashFlowData,
  center,
  convertExpensesToNodes,
  extractDriversAndMerchantsEdges,
  initialNodes,
  labelStyle,
  nodeTypes,
  normalizeBySum,
  offsetX,
  offsetY,
} from './utils';
import DoubleDatePicker from '../../../common/double-date-picker/double-date-picker';
import FilterChips from '../../../common/filter-chips/filter-chips';
import AutoFitView from './auto-fit';
import { IconWithText } from '../../../common/styles/components';

enum DateFilterInputMode {
  CURRENT = 'Current Month',
  PREVIOUS = 'Previous Month',
  CUSTOM = 'Custom Range',
}
function CashFlow() {
  const [nodes, setNodes, onNodesChange] = useNodesState([] as Node[]);
  const [cashFlowData, setCashFlowData] = useState<CashFlowData>();
  const { showMessage } = useSnackBar();
  const [isLoading, setIsLoading] = useState(false);
  const { config } = useContext(AuthContext);
  const [dateFilter, setDateFilter] = useState<DoubleDate>({
    startDate: dayjs().startOf('month'),
    endDate: dayjs(),
  });
  const [isExporting, setIsExporting] = useState(false)

  const [filterMode, setFilterMode] = useState<DateFilterInputMode>(
    DateFilterInputMode.CURRENT
  );

  const [edges, setEdges, onEdgesChange] = useEdgesState([] as Edge[]);
  const [showIndividualDrivers, setShowIndividualDrivers] = useState(false);
  const [showIndividualMerchants, setShowIndividualMerchants] = useState(false);
  const [showIndividualExpenses, setShowIndividualExpenses] = useState(false);

  const toggleDriverVisibility = () => {
    setShowIndividualDrivers((prev) => !prev);
  };

  const toggleMerchanVisibility = () => {
    setShowIndividualMerchants((prev) => !prev);
  };

  const toggleExpensesVisibility = () => {
    setShowIndividualExpenses((prev) => !prev);
  };

  useEffect(() => {
    const fetchData = async () => {
      if (!dateFilter.startDate || !dateFilter.endDate) {
        showMessage('Please select a date range to proceed', 'error');
        return;
      }
      setIsLoading(true);
      let endpoint = `${API_ENDPOINT}/v1/cash-flow/`;
      endpoint += `?date_from=${encodeURIComponent(dateFilter.startDate.format('YYYY-MM-DD'))}`;
      endpoint += `&date_to=${encodeURIComponent(dateFilter.endDate.format('YYYY-MM-DD'))}`;
      try {
        const response = await axios.get<CashFlowData>(endpoint, config);
        setCashFlowData(response.data);
      } catch {
        showMessage('Failed to load data', 'error');
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
  }, [JSON.stringify(dateFilter)]);

  useEffect(() => {
    if (cashFlowData) {
      const accountingNode = {
        id: 'accounting',
        type: 'accounting',
        position: { x: center.x, y: center.y },
        data: { label: 'Accounting', cashFlowData: cashFlowData },
      };

      const driverDetails = cashFlowData.drivers.details;
      const spacing = 190;
      const startY = center.y - ((driverDetails.length - 1) * spacing) / 2;

      const normalizedDriversProportions = normalizeBySum(
        driverDetails.map((detail) => moneyToMetric(detail.amount))
      );

      const normalizedDriversProfitsProportions = normalizeBySum(
        driverDetails.map((detail) => moneyToMetric(detail.profit))
      );

      const driverNodes: Node[] = showIndividualDrivers
        ? driverDetails.map((driverDetail, i) => ({
            id: `driver-${driverDetail.driver.id}`,
            type: 'driver',
            position: {
              x: center.x + offsetX + 550,
              y: startY + i * spacing,
            },
            data: {
              label: driverDetail.driver.name,
              importance: normalizedDriversProportions[i],
              profitImportance: normalizedDriversProfitsProportions[i],
              ...driverDetail,
            },
          }))
        : [];

      const driversAggregateNode: Node = {
        id: 'drivers',
        type: 'driver',
        position: { x: center.x + offsetX, y: center.y },
        data: {
          isAggregate: true,
          label: 'Total Driver Earnings',
          expanded: showIndividualDrivers,
          onToggleExpand: toggleDriverVisibility,
          total: cashFlowData.drivers.total,
          profit: cashFlowData.drivers.profit
        },
      };

      const merchantsAggregateNode: Node = {
        id: 'merchants',
        type: 'merchant',
        position: { x: center.x - offsetX, y: center.y },
        data: {
          isAggregate: true,
          label: 'Total Merchants Payments',
          expanded: showIndividualMerchants,
          onToggleExpand: toggleMerchanVisibility,
          amount: cashFlowData.merchants.total,
          pending_amount: cashFlowData.merchants.pending_total
        },
      };

      const merchantStartY =
        center.y - ((cashFlowData.merchants.details.length - 1) * spacing) / 2;

      const normalizedMerchantsProportions = normalizeBySum(
        cashFlowData.merchants.details.map((detail) =>
          moneyToMetric(detail.amount)
        )
      );

      const normalizedMerchantsPendingProportions = normalizeBySum(
        cashFlowData.merchants.details.map((detail) =>
          moneyToMetric(detail.pending_amount)
        )
      );

      const merchantNodes = showIndividualMerchants
        ? cashFlowData.merchants.details.map((merchantDetail, i) => ({
            id: `merchant-${merchantDetail.merchant.id}`,
            type: 'merchant',
            position: {
              x: merchantsAggregateNode.position.x - 450,
              y: merchantStartY + i * spacing,
            },
            data: {
              isAggregate: false,
              label: merchantDetail.merchant.name,
              importance: normalizedMerchantsProportions[i],
              pendingImportance: normalizedMerchantsPendingProportions[i],
              ...merchantDetail,
            },
          }))
        : [];

      const expensesAggregateNode: Node = {
        id: 'expenses',
        type: 'expense',
        position: { x: center.x, y: center.y + offsetY },
        data: {
          isAggregate: true,
          label: 'Total Expenses',
          expanded: showIndividualExpenses,
          onToggleExpand: toggleExpensesVisibility,
        },
      };

      const expenseNodes = showIndividualExpenses
        ? convertExpensesToNodes(
            cashFlowData.expenses.details,
            expensesAggregateNode
          )
        : [];

      setNodes([
        accountingNode,
        driversAggregateNode,
        merchantsAggregateNode,
        expensesAggregateNode,
        ...driverNodes,
        ...merchantNodes,
        ...expenseNodes,
      ]);
    }
  }, [
    cashFlowData,
    showIndividualDrivers,
    showIndividualMerchants,
    showIndividualExpenses,
  ]);

  useEffect(() => {
    if (cashFlowData) {
      const expensesNodes = nodes.filter((node) => node.type === 'expense');

      const driverEdges: Edge[] = cashFlowData.drivers.details.map(
        (driverDetail: { driver: DriverType; amount: Money[] }) => ({
          id: `edge-driver-${driverDetail.driver.id}`,
          source: `driver-${driverDetail.driver.id}`,
          target: 'drivers',
          sourceHandle: 'source',
          targetHandle: 'sink',
          animated: true,
          label: displayMoney(driverDetail.amount),
          labelStyle: labelStyle,
          markerEnd: {
            type: MarkerType.ArrowClosed,
            width: 20,
            height: 20,
            color: '#222',
          },
          style: {
            stroke: '#2196f3',
          },
        })
      );

      const merchantEdges = showIndividualMerchants
        ? cashFlowData.merchants.details.map((merchantDetail) => ({
            id: `edge-merchant-${merchantDetail.merchant.id}`,
            target: `merchant-${merchantDetail.merchant.id}`,
            source: 'merchants',
            sourceHandle: 'source',
            targetHandle: 'sink',
            animated: true,
            labelStyle,
            markerEnd: {
              type: MarkerType.ArrowClosed,
              width: 20,
              height: 20,
              color: '#d81b60',
            },
            style: {
              stroke: '#ec407a',
            },
          }))
        : [];

      const accountingEdges = extractDriversAndMerchantsEdges(cashFlowData);

      const expenseEdges: Edge[] = showIndividualExpenses
        ? expensesNodes
            .filter((expense) => !expense.data.isAggregate)
            .map((expense) => {
              console.log(expense);
              return {
                id: `aggregated-expense-expense-${expense.id}`,
                source: 'expenses',
                target: expense.id,
                sourceHandle: 'bottom-source',
                targetHandle: 'top-sink',
                label: displayMoney((expense.data.expense as Expense).expense),
                labelStyle: labelStyle,
                animated: true,
                markerEnd: {
                  type: MarkerType.ArrowClosed,
                  width: 20,
                  height: 20,
                  color: '#222',
                },
              };
            })
        : [];

      setEdges([
        ...accountingEdges,
        ...merchantEdges,
        ...expenseEdges,
        ...driverEdges,
      ]);
    }
  }, [nodes, cashFlowData]);

  useEffect(() => {
    if (filterMode == DateFilterInputMode.CURRENT)
      setDateFilter({
        startDate: dayjs().startOf('month'),
        endDate: dayjs(),
      });

    if (filterMode == DateFilterInputMode.PREVIOUS)
      setDateFilter({
        startDate: dayjs().subtract(1, 'month').startOf('month'),
        endDate: dayjs().subtract(1, 'month').endOf('month'),
      });
  }, [filterMode]);
  console.log('nodes = ', nodes);
  console.log('edges = ', edges);
  
  const exportCashFlow = async () => {
    if (!dateFilter.startDate || !dateFilter.endDate) {
      showMessage("Please select start and end date", 'error');
      return;
    }

    let endpoint = `${API_ENDPOINT}/v1/cash-flow/export/`;
    endpoint += `?date_from=${encodeURIComponent(dateFilter.startDate.format('YYYY-MM-DD'))}`;
    endpoint += `&date_to=${encodeURIComponent(dateFilter.endDate.format('YYYY-MM-DD'))}`;

    try {
      setIsExporting(true);

      const response = await axios.get(endpoint, {
        responseType: 'blob',  // Required for file download
        ...config
      });

      // Get filename from Content-Disposition header or use default
      const contentDisposition = response.headers['content-disposition'];
      let filename = 'cash_flow_reports.zip'; // Default fallback
      
      if (contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename="?(.+)"?/);
        if (filenameMatch && filenameMatch[1]) {
          filename = filenameMatch[1];
        }
      }

      const blob = new Blob([response.data], {
        type: 'application/zip', // Changed from Excel MIME type to ZIP
      });

      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename; // Use the extracted filename
      document.body.appendChild(link); // Required for Firefox
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      showMessage("Exported successfully");
    } catch (error) {
      console.error("Export error:", error);
      showMessage("Failed to export cash flow reports", 'error');
    } finally {
      setIsExporting(false);
    }
};

  return (
    <>

      <FilterChips
        label={''}
        options={Array.from(Object.values(DateFilterInputMode))}
        selected={[filterMode]}
        onChange={(selected) => {
          if (selected.length > 0)
            setFilterMode(selected[0] as DateFilterInputMode);
        }}
      />

      {filterMode == DateFilterInputMode.CUSTOM && (
        <div style={{ margin: '15px', maxWidth: 550 }}>
          <DoubleDatePicker
            defaultDoubleDate={dateFilter}
            headless
            disableClear
            dateFilterAction={(d: DoubleDate) => setDateFilter(d)}
          />
        </div>
      )}

      <div
        style={{
          height: `calc(100vh - 320px)`,
          backgroundColor: '#f5f5f5',
          margin: '10px',
          boxSizing: 'border-box',
        }}
      >
        {isLoading ? (
          <div
            style={{
              height: '100%',
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
            }}
          >
            <CircularProgress />
          </div>
        ) : (
          <ReactFlow
            nodes={nodes}
            edges={edges}
            onNodesChange={onNodesChange}
            onEdgesChange={onEdgesChange}
            nodeTypes={nodeTypes}
            fitView
            fitViewOptions={{ padding: 0.2 }}
            style={{
              backgroundColor: '#ffffff',
              borderRadius: '12px',
              boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
            }}
          >
            <div style={{position: "absolute", right: 10, top: 10, zIndex: 100000}}>
              <Button variant='outlined' disabled={isExporting} onClick={exportCashFlow}> <IconWithText centered>{isExporting ? <CircularProgress size={20} /> : <CloudDownloadIcon />} Export XLSX </IconWithText> </Button>
            </div>
            <AutoFitView
              dependencies={[
                showIndividualDrivers,
                showIndividualMerchants,
                showIndividualExpenses,
                nodes,
              ]}
            />
            <Controls />
            
            <Background variant={BackgroundVariant.Dots} gap={12} size={1} />
          </ReactFlow>
        )}
      </div>
    </>
  );
}

export default CashFlow;
